import React, { useEffect, useState } from 'react'
import Topbar from '../components/Layout/Topbar'
import { Sidebar } from '../components/Layout/Sidebar'
import { Link } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { supabase } from '../supabase.config'
import 'chart.js/auto'
import { Doughnut, Line } from 'react-chartjs-2'
import {
  FaArrowDown,
  FaArrowUp,
  FaBox,
  FaCheckCircle,
  FaClock,
  FaDollarSign,
  FaRoute,
} from 'react-icons/fa'
import { RiTakeawayFill } from 'react-icons/ri'
import { TbTruckDelivery } from 'react-icons/tb'
import { HiOutlineTruck } from 'react-icons/hi2'

const parseJSON = (value) => {
  if (!value) return null
  if (typeof value === 'object') return value
  try {
    return JSON.parse(value)
  } catch {
    return null
  }
}

const getStatusKey = (status) => {
  const value = String(status || '').trim().toLowerCase()

  if (['delivered'].includes(value)) return 'delivered'
  if (['in-transit', 'in transit', 'pickedup', 'picked up', 'out for delivery'].includes(value)) return 'inTransit'
  if (['undelivered', 'delayed', 'delay'].includes(value)) return 'delayed'
  return 'other'
}

const getStatusMeta = (status) => {
  const key = getStatusKey(status)

  if (key === 'delivered') {
    return {
      label: 'Delivered',
      tone: 'text-[#ea580c]',
      bg: 'bg-[#fff1e8]',
      track: 'bg-[#ffd6bf]',
      dot: 'bg-[#ea580c]',
    }
  }

  if (key === 'inTransit') {
    return {
      label: 'In Transit',
      tone: 'text-[#f97316]',
      bg: 'bg-[#fff4ed]',
      track: 'bg-[#ffe2cf]',
      dot: 'bg-[#f97316]',
    }
  }

  if (key === 'delayed') {
    return {
      label: 'Delayed',
      tone: 'text-[#c2410c]',
      bg: 'bg-[#ffede2]',
      track: 'bg-[#ffc8ab]',
      dot: 'bg-[#c2410c]',
    }
  }

  return {
    label: status || 'Pending',
    tone: 'text-slate-500',
    bg: 'bg-slate-100',
    track: 'bg-slate-200',
    dot: 'bg-slate-400',
  }
}

const startOfDay = (date) => {
  const next = new Date(date)
  next.setHours(0, 0, 0, 0)
  return next
}

const differenceInDays = (first, second) => {
  return Math.round((startOfDay(first) - startOfDay(second)) / 86400000)
}

const formatCompactDate = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return 'No date'
  return new Intl.DateTimeFormat('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(date)
}

const formatCompactTime = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '--:--'
  return new Intl.DateTimeFormat('en-IN', {
    hour: '2-digit',
    minute: '2-digit',
  }).format(date)
}

const formatRoute = (pickupCity, dropCity) => {
  const from = pickupCity || 'Origin'
  const to = dropCity || 'Destination'
  return `${from} → ${to}`
}

const getOrderAge = (createdAt) => {
  if (!(createdAt instanceof Date) || Number.isNaN(createdAt.getTime())) return 0
  return Math.max(differenceInDays(new Date(), createdAt), 0)
}

const getTrend = (items, matcher) => {
  const today = startOfDay(new Date())
  const currentStart = new Date(today)
  currentStart.setDate(today.getDate() - 6)
  const previousStart = new Date(today)
  previousStart.setDate(today.getDate() - 13)
  const previousEnd = new Date(today)
  previousEnd.setDate(today.getDate() - 7)

  const current = items.filter((item) => {
    if (!matcher(item)) return false
    return item.createdAt >= currentStart && item.createdAt <= today
  }).length

  const previous = items.filter((item) => {
    if (!matcher(item)) return false
    return item.createdAt >= previousStart && item.createdAt <= previousEnd
  }).length

  const change = previous === 0 ? (current > 0 ? 100 : 0) : ((current - previous) / previous) * 100

  return {
    current,
    previous,
    change,
    positive: change >= 0,
  }
}

const buildWeeklySeries = (items) => {
  const today = startOfDay(new Date())
  const labels = []
  const values = []

  for (let offset = 6; offset >= 0; offset -= 1) {
    const day = new Date(today)
    day.setDate(today.getDate() - offset)
    const label = day.toLocaleDateString('en-IN', { weekday: 'short' })
    labels.push(label)
    values.push(
      items.filter((item) => differenceInDays(day, item.createdAt) === 0).length
    )
  }

  return { labels, values }
}

const buildSparklinePath = (values, width = 180, height = 44) => {
  if (!values.length) return ''

  const min = Math.min(...values)
  const max = Math.max(...values)
  const range = max - min || 1

  return values
    .map((value, index) => {
      const x = (index / Math.max(values.length - 1, 1)) * width
      const y = height - ((value - min) / range) * (height - 8) - 4
      return `${index === 0 ? 'M' : 'L'} ${x.toFixed(2)} ${y.toFixed(2)}`
    })
    .join(' ')
}

const StatCard = ({ label, value, delta, icon, iconWrap, iconColor }) => {
  const Arrow = delta.positive ? FaArrowUp : FaArrowDown
  const trendTone = delta.positive ? 'text-[#ea580c]' : 'text-[#f97316]'

  return (
    <div className='rounded-[28px] border border-[rgba(15,23,42,0.06)] bg-white px-6 py-5 shadow-[0_16px_40px_rgba(15,23,42,0.06)]'>
      <div className='flex items-start justify-between gap-4'>
        <div>
          <p className='text-sm font-[GilroyBold] text-slate-700'>{label}</p>
          <p className='mt-3 text-[2rem] leading-none font-[GilroyBold] tracking-[-0.05em] text-slate-950'>{value}</p>
          <div className='mt-4 flex flex-wrap items-center gap-2 text-sm font-[GilroyMedium]'>
            <span className={`inline-flex items-center gap-2 rounded-full px-3 py-1.5 ${delta.positive ? 'bg-[#fff1e8]' : 'bg-[#ffede2]'} ${trendTone}`}>
              <Arrow className='text-xs' />
              <span className='font-[GilroyBold]'>{Math.abs(delta.change).toFixed(1)}%</span>
            </span>
            <span className='whitespace-nowrap text-slate-400'>vs last week</span>
          </div>
        </div>
        <div className={`flex h-16 w-16 items-center justify-center rounded-full ${iconWrap}`}>
          <div className={`text-[1.45rem] ${iconColor}`}>{icon}</div>
        </div>
      </div>
    </div>
  )
}

const SectionCard = ({ title, action, children, className = '' }) => (
  <div className={`rounded-[30px] border border-[rgba(15,23,42,0.06)] bg-white p-6 shadow-[0_18px_44px_rgba(15,23,42,0.06)] ${className}`}>
    <div className='mb-6 flex items-center justify-between gap-4'>
      <h2 className='text-[1.45rem] font-[GilroyBold] tracking-[-0.03em] text-slate-950'>{title}</h2>
      {action}
    </div>
    {children}
  </div>
)

const TinyPerformanceCard = ({ title, value, changeText, icon, lineColor, values }) => (
  <div className='rounded-[24px] border border-[rgba(15,23,42,0.06)] bg-white p-5 shadow-[0_14px_32px_rgba(15,23,42,0.05)]'>
    <div className='flex items-start justify-between gap-4'>
      <div>
        <p className='text-sm font-[GilroyBold] text-slate-600'>{title}</p>
        <p className='mt-2 text-[2rem] leading-none font-[GilroyBold] tracking-[-0.05em] text-slate-950'>{value}</p>
        <p className='mt-3 text-sm font-[GilroyMedium] text-slate-400'>{changeText}</p>
      </div>
      <div className='flex h-11 w-11 items-center justify-center rounded-full bg-slate-50 text-slate-500'>
        {icon}
      </div>
    </div>
    <svg viewBox='0 0 180 44' className='mt-4 h-12 w-full overflow-visible'>
      <path
        d={buildSparklinePath(values)}
        fill='none'
        stroke={lineColor}
        strokeWidth='2.75'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
    </svg>
  </div>
)

const Home = () => {
  const { users, userProfile } = useSelector((state) => state.user)
  const isDeliveryUser = ['Delivery Boy', 'Delivery Partner'].includes(userProfile?.role)
  const [orders, setOrders] = useState([])

  useEffect(() => {
    const fetchOrders = async () => {
      if (!userProfile?.role) return

      let query = supabase.from('orders').select('*').order('created_at', { ascending: false })

      if (userProfile?.role !== 'admin' && users) {
        query = query.eq('user_id', users)
      }

      const { data, error } = await query
      if (error) return

      const mapped = (data || []).map((row) => {
        const pkg = parseJSON(row.package_details) || {}
        const pickup = parseJSON(row.pickup_address) || row.pickup_location || {}
        const drop = parseJSON(row.delivery_address) || row.dropoff_location || {}
        const dimensions = parseJSON(row.dimensions) || []
        const createdAt = row.created_at ? new Date(row.created_at) : new Date()
        const promiseDate = pkg.deliveryactualdate ? new Date(pkg.deliveryactualdate) : null
        const boxes = dimensions.reduce((sum, item) => sum + Number(item?.count || 0), 0)
        const chargeableWeight = dimensions.reduce((sum, item) => (
          sum + ((((Number(item?.length) || 0) * (Number(item?.width) || 0) * (Number(item?.height) || 0)) / 4500) * (Number(item?.count) || 0))
        ), 0)

        return {
          id: row.id,
          lrno: row.tracking_number || row.order_number || row.order_id || 'N/A',
          userName: pkg.userName || '',
          orderStatus: row.status || 'pending',
          statusKey: getStatusKey(row.status),
          createdAt,
          promiseDate,
          pickupCity: pickup?.city || pickup?.pickupName?.city || '',
          dropCity: drop?.city || '',
          route: formatRoute(pickup?.city || pickup?.pickupName?.city || '', drop?.city || ''),
          consignee: drop?.consignee || drop?.companyName || '',
          weight: Number(row.weight || 0),
          chargeableWeight: Math.ceil(chargeableWeight || 0),
          boxes,
        }
      })

      setOrders(mapped)
    }

    fetchOrders()
  }, [userProfile, users])

  const deliveredOrders = orders.filter((item) => item.statusKey === 'delivered')
  const inTransitOrders = orders.filter((item) => item.statusKey === 'inTransit')
  const delayedOrders = orders.filter((item) => {
    if (item.statusKey === 'delayed') return true
    if (item.statusKey === 'delivered') return false
    if (!item.promiseDate || Number.isNaN(item.promiseDate.getTime())) return false
    return differenceInDays(new Date(), item.promiseDate) > 0
  })

  const totalShipments = orders.length
  const weeklySeries = buildWeeklySeries(orders)
  const topRoutes = Object.values(
    orders.reduce((acc, item) => {
      if (!acc[item.route]) {
        acc[item.route] = { route: item.route, count: 0 }
      }
      acc[item.route].count += 1
      return acc
    }, {})
  )
    .sort((left, right) => right.count - left.count)
    .slice(0, 5)

  const recentShipments = [...orders].slice(0, 5)
  const liveShipments = [...inTransitOrders]
    .sort((left, right) => right.createdAt - left.createdAt)
    .slice(0, 5)

  const deliveredRate = totalShipments ? (deliveredOrders.length / totalShipments) * 100 : 0
  const avgWeight = totalShipments ? orders.reduce((sum, item) => sum + item.weight, 0) / totalShipments : 0
  const avgBoxes = totalShipments ? orders.reduce((sum, item) => sum + item.boxes, 0) / totalShipments : 0
  const activeRoutes = new Set(orders.map((item) => item.route)).size
  const avgTransitAge = inTransitOrders.length
    ? inTransitOrders.reduce((sum, item) => sum + getOrderAge(item.createdAt), 0) / inTransitOrders.length
    : 0

  const totalTrend = getTrend(orders, () => true)
  const deliveredTrend = getTrend(orders, (item) => item.statusKey === 'delivered')
  const transitTrend = getTrend(orders, (item) => item.statusKey === 'inTransit')
  const delayedTrend = getTrend(orders, (item) => delayedOrders.some((delayed) => delayed.id === item.id))

  const statusData = {
    labels: ['Delivered', 'In Transit', 'Delayed'],
    datasets: [
      {
        data: [deliveredOrders.length, inTransitOrders.length, delayedOrders.length],
        backgroundColor: ['#fb923c', '#f97316', '#c2410c'],
        borderWidth: 0,
        hoverOffset: 6,
      },
    ],
  }

  const statusLegend = [
    { label: 'Delivered', value: deliveredOrders.length, color: '#fb923c' },
    { label: 'In Transit', value: inTransitOrders.length, color: '#f97316' },
    { label: 'Delayed', value: delayedOrders.length, color: '#c2410c' },
  ]

  const overviewData = {
    labels: weeklySeries.labels,
    datasets: [
      {
        label: 'Shipments',
        data: weeklySeries.values,
        borderColor: '#ff6b2c',
        backgroundColor: (context) => {
          const chart = context.chart
          const { ctx, chartArea } = chart
          if (!chartArea) return 'rgba(255,107,44,0.18)'
          const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom)
          gradient.addColorStop(0, 'rgba(255,107,44,0.24)')
          gradient.addColorStop(1, 'rgba(255,107,44,0.02)')
          return gradient
        },
        fill: true,
        tension: 0.45,
        pointRadius: 0,
        pointHoverRadius: 5,
        pointHoverBackgroundColor: '#ffffff',
        pointHoverBorderColor: '#ff6b2c',
        pointHoverBorderWidth: 3,
      },
    ],
  }

  const performanceLines = {
    deliveredRate: weeklySeries.values.map((value, index) => value + (index % 2 === 0 ? 1 : 4)),
    avgWeight: weeklySeries.values.map((value, index) => value + 8 + (index % 3)),
    routes: weeklySeries.values.map((value, index) => Math.max(2, value / 2 + index)),
    boxes: weeklySeries.values.map((value, index) => value * 2 + 6 + (index % 2)),
  }

  return (
    <div className='crm-app-shell flex min-h-screen gap-3 p-3'>
      {!isDeliveryUser && <Sidebar />}
      {!isDeliveryUser && (
        <div className='crm-page-scroll flex flex-1 flex-col'>
          <Topbar />
          <div className='crm-page-body flex-1'>
            <div className='grid grid-cols-1 gap-4 xl:grid-cols-4'>
              <StatCard
                label='Total Shipments'
                value={totalShipments.toLocaleString('en-IN')}
                delta={totalTrend}
                icon={<FaBox />}
                iconWrap='bg-[#fff1e8]'
                iconColor='text-[#ff6b2c]'
              />
              <StatCard
                label='Delivered'
                value={deliveredOrders.length.toLocaleString('en-IN')}
                delta={deliveredTrend}
                icon={<FaCheckCircle />}
                iconWrap='bg-[#fff1e8]'
                iconColor='text-[#ea580c]'
              />
              <StatCard
                label='In Transit'
                value={inTransitOrders.length.toLocaleString('en-IN')}
                delta={transitTrend}
                icon={<HiOutlineTruck />}
                iconWrap='bg-[#fff4ed]'
                iconColor='text-[#f97316]'
              />
              <StatCard
                label='Delayed'
                value={delayedOrders.length.toLocaleString('en-IN')}
                delta={delayedTrend}
                icon={<FaClock />}
                iconWrap='bg-[#ffede2]'
                iconColor='text-[#c2410c]'
              />
            </div>

            <div className='mt-6 grid grid-cols-1 gap-6 xl:grid-cols-2'>
              <SectionCard
                title='Shipment Overview'
                action={<div className='rounded-xl bg-[#fff4ed] px-3 py-2 text-sm font-[GilroyBold] text-[#f97316]'>This Week</div>}
              >
                <div className='h-[310px]'>
                  <Line
                    data={overviewData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: { display: false },
                        tooltip: {
                          backgroundColor: 'rgba(15,23,42,0.92)',
                          displayColors: false,
                          titleFont: { family: 'GilroyBold', size: 13 },
                          bodyFont: { family: 'GilroyMedium', size: 12 },
                          padding: 12,
                        },
                      },
                      scales: {
                        x: {
                          grid: { display: false },
                          border: { display: false },
                          ticks: {
                            color: '#94a3b8',
                            font: { family: 'GilroyMedium', size: 12 },
                          },
                        },
                        y: {
                          beginAtZero: true,
                          grid: { color: 'rgba(148,163,184,0.12)' },
                          border: { display: false },
                          ticks: {
                            color: '#94a3b8',
                            font: { family: 'GilroyMedium', size: 12 },
                            precision: 0,
                          },
                        },
                      },
                    }}
                  />
                </div>
              </SectionCard>

              <SectionCard
                title='Live Tracking'
                action={<Link to='/orders' className='text-sm font-[GilroyBold] text-[#ff6b2c]'>View all</Link>}
              >
                <div className='space-y-5'>
                  {liveShipments.length > 0 ? liveShipments.map((item) => {
                    const meta = getStatusMeta(item.orderStatus)
                    return (
                      <div key={item.id} className='flex items-start gap-4'>
                        <div className={`mt-1 flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl ${meta.bg}`}>
                          <HiOutlineTruck className={`${meta.tone} text-lg`} />
                        </div>
                        <div className='min-w-0 flex-1'>
                          <div className='flex items-start justify-between gap-3'>
                            <div>
                              <p className='truncate text-sm font-[GilroyBold] text-slate-900'>{item.lrno}</p>
                              <p className='mt-1 text-sm font-[GilroyMedium] text-slate-500'>{item.route}</p>
                            </div>
                            <p className={`whitespace-nowrap text-sm font-[GilroyBold] ${meta.tone}`}>{meta.label}</p>
                          </div>
                          <div className={`mt-3 h-1.5 rounded-full ${meta.track}`}>
                            <div
                              className={`h-full rounded-full ${meta.dot}`}
                              style={{ width: `${Math.min(92, 30 + getOrderAge(item.createdAt) * 12)}%` }}
                            />
                          </div>
                        </div>
                      </div>
                    )
                  }) : (
                    <p className='text-sm font-[GilroyMedium] text-slate-500'>No active shipments in transit right now.</p>
                  )}
                </div>
              </SectionCard>
            </div>

            <div className='mt-6 grid grid-cols-1 gap-6 xl:grid-cols-2'>
              <SectionCard title='Shipments by Status'>
                <div className='flex h-full flex-col justify-center gap-6 lg:flex-row lg:items-center xl:flex-col xl:items-stretch'>
                  <div className='relative mx-auto flex h-[240px] w-[240px] items-center justify-center'>
                    <Doughnut
                      data={statusData}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        cutout: '74%',
                        plugins: {
                          legend: { display: false },
                          tooltip: {
                            backgroundColor: 'rgba(15,23,42,0.92)',
                            titleFont: { family: 'GilroyBold', size: 13 },
                            bodyFont: { family: 'GilroyMedium', size: 12 },
                            padding: 12,
                          },
                        },
                      }}
                    />
                    <div className='pointer-events-none absolute inset-0 flex flex-col items-center justify-center'>
                      <p className='text-[2.1rem] leading-none font-[GilroyBold] tracking-[-0.05em] text-slate-950'>
                        {totalShipments.toLocaleString('en-IN')}
                      </p>
                      <p className='mt-2 text-sm font-[GilroyMedium] text-slate-500'>Total</p>
                    </div>
                  </div>
                  <div className='space-y-4'>
                    {statusLegend.map((item) => {
                      const percentage = totalShipments ? ((item.value / totalShipments) * 100).toFixed(1) : '0.0'
                      return (
                        <div key={item.label} className='flex items-center justify-between gap-5'>
                          <div className='flex items-center gap-3'>
                            <span className='h-3 w-3 rounded-full' style={{ backgroundColor: item.color }} />
                            <p className='text-sm font-[GilroyBold] text-slate-700'>{item.label}</p>
                          </div>
                          <p className='text-sm font-[GilroyMedium] text-slate-500'>
                            {item.value} ({percentage}%)
                          </p>
                        </div>
                      )
                    })}
                  </div>
                </div>
              </SectionCard>

              <SectionCard
                title='Top Routes'
                action={<Link to='/orders' className='text-sm font-[GilroyBold] text-[#ff6b2c]'>View all</Link>}
              >
                <div className='space-y-5'>
                  {topRoutes.length > 0 ? topRoutes.map((item) => {
                    const percentage = topRoutes[0]?.count ? (item.count / topRoutes[0].count) * 100 : 0
                    return (
                      <div key={item.route}>
                        <div className='flex items-center justify-between gap-4'>
                          <p className='text-sm font-[GilroyBold] text-slate-800'>{item.route}</p>
                          <p className='text-sm font-[GilroyBold] text-slate-600'>{item.count}</p>
                        </div>
                        <div className='mt-3 h-1.5 rounded-full bg-[#ffe7dc]'>
                          <div className='h-full rounded-full bg-[#ff6b2c]' style={{ width: `${percentage}%` }} />
                        </div>
                      </div>
                    )
                  }) : (
                    <p className='text-sm font-[GilroyMedium] text-slate-500'>No route data available yet.</p>
                  )}
                </div>
              </SectionCard>
            </div>

            <div className='mt-6 grid grid-cols-1 gap-6 xl:grid-cols-2'>
              <SectionCard
                title='Recent Shipments'
                action={<Link to='/orders' className='text-sm font-[GilroyBold] text-[#ff6b2c]'>View all</Link>}
              >
                <div className='space-y-5'>
                  {recentShipments.length > 0 ? recentShipments.map((item) => {
                    const meta = getStatusMeta(item.orderStatus)
                    return (
                      <div key={item.id} className='flex items-start gap-4'>
                        <div className={`mt-1 flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl ${meta.bg}`}>
                          <TbTruckDelivery className={`${meta.tone} text-lg`} />
                        </div>
                        <div className='min-w-0 flex-1'>
                          <div className='flex items-start justify-between gap-3'>
                            <div>
                              <p className='truncate text-sm font-[GilroyBold] text-slate-900'>{item.lrno}</p>
                              <p className='mt-1 text-sm font-[GilroyMedium] text-slate-500'>{item.route}</p>
                            </div>
                            <p className={`whitespace-nowrap text-sm font-[GilroyBold] ${meta.tone}`}>{meta.label}</p>
                          </div>
                          <p className='mt-2 text-sm font-[GilroyMedium] text-slate-400'>
                            {formatCompactDate(item.createdAt)}, {formatCompactTime(item.createdAt)}
                          </p>
                        </div>
                      </div>
                    )
                  }) : (
                    <p className='text-sm font-[GilroyMedium] text-slate-500'>No shipments found.</p>
                  )}
                </div>
              </SectionCard>

              <SectionCard
                title='Performance'
                action={<div className='rounded-xl bg-[#fff4ed] px-3 py-2 text-sm font-[GilroyBold] text-[#f97316]'>This Month</div>}
              >
                <div className='grid gap-4 sm:grid-cols-2'>
                  <TinyPerformanceCard
                    title='Delivery Rate'
                    value={`${deliveredRate.toFixed(1)}%`}
                    changeText={`${deliveredOrders.length} delivered shipments`}
                    icon={<FaCheckCircle />}
                    lineColor='#fb923c'
                    values={performanceLines.deliveredRate}
                  />
                  <TinyPerformanceCard
                    title='Avg. Shipment Weight'
                    value={`${avgWeight.toFixed(1)} kg`}
                    changeText='Across all current orders'
                    icon={<TbTruckDelivery />}
                    lineColor='#f97316'
                    values={performanceLines.avgWeight}
                  />
                  <TinyPerformanceCard
                    title='Active Routes'
                    value={activeRoutes.toLocaleString('en-IN')}
                    changeText='Unique lanes in the dashboard'
                    icon={<FaRoute />}
                    lineColor='#ff8b5f'
                    values={performanceLines.routes}
                  />
                  <TinyPerformanceCard
                    title='Avg. Boxes / Shipment'
                    value={avgBoxes.toFixed(1)}
                    changeText={`${avgTransitAge.toFixed(1)} day avg. transit age`}
                    icon={<FaDollarSign />}
                    lineColor='#4e7bff'
                    values={performanceLines.boxes}
                  />
                </div>
              </SectionCard>
            </div>
          </div>
        </div>
      )}
      {isDeliveryUser && <Sidebar />}
      {isDeliveryUser && (
        <div className='crm-page-scroll flex flex-1 flex-col'>
          <Topbar />
          <div className='crm-page-body flex flex-1 items-center justify-center'>
            <div className='flex w-full max-w-6xl flex-col gap-8'>
              <div className='grid grid-cols-1 gap-6 lg:grid-cols-3'>
                <Link to='/createDelivery'>
                  <div className='crm-soft-panel flex flex-col items-center justify-center rounded-[28px] px-6 py-5 transition-transform duration-200 hover:-translate-y-1'>
                    <div className='flex items-center justify-between gap-5'>
                      <h3 className='text-[1.3rem] font-[GilroyBold]'>Create Order</h3>
                      <div className='flex h-[70px] w-[70px] items-center justify-center rounded-[22px] bg-[#fff1e8]'>
                        <FaBox className='text-[40px] text-[#f97316]' />
                      </div>
                    </div>
                  </div>
                </Link>
                <Link to='/deliveryList'>
                  <div className='crm-soft-panel flex flex-col items-center justify-center rounded-[28px] px-6 py-5 transition-transform duration-200 hover:-translate-y-1'>
                    <div className='flex items-center justify-between gap-5'>
                      <h3 className='text-[1.3rem] font-[GilroyBold]'>Delivery List</h3>
                      <div className='flex h-[70px] w-[70px] items-center justify-center rounded-[22px] bg-[#fff4ed]'>
                        <TbTruckDelivery className='text-[40px] text-[#ea580c]' />
                      </div>
                    </div>
                  </div>
                </Link>
                <Link to='/pickupList'>
                  <div className='crm-soft-panel flex flex-col items-center justify-center rounded-[28px] px-6 py-5 transition-transform duration-200 hover:-translate-y-1'>
                    <div className='flex items-center justify-between gap-5'>
                      <h3 className='text-[1.3rem] font-[GilroyBold]'>Pickup List</h3>
                      <div className='flex h-[70px] w-[70px] items-center justify-center rounded-[22px] bg-[#ffede2]'>
                        <RiTakeawayFill className='text-[40px] text-[#c2410c]' />
                      </div>
                    </div>
                  </div>
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Home
